import { api } from './client'

// ============================================================================
// SETTINGS TYPES
// ============================================================================

// Tenant Info (from tenant table, read-only)
export interface TenantInfo {
  code: string
  currency: string
}

// Branding Settings (White-label customization)
export interface BrandingSettings {
  platform_name?: string
  company_name?: string
  logo_url?: string
  favicon_url?: string
  primary_color?: string
  admin_email?: string
  support_email?: string
  support_url?: string
  timezone?: string
  language?: string // UI language: 'en', 'zh-CN', 'zh-HK'
}

// Legacy alias for platform settings
export type PlatformSettings = BrandingSettings

// OpenRTB Settings
// Note: Currency is managed at tenant level (tenants.currency), not here
export interface OpenRTBSettings {
  default_openrtb_version?: string
  default_auction_type?: number
  default_tmax?: number
}

// Traffic Settings
export interface TrafficSettings {
  global_qps_limit?: number
}

// Ad Quality Settings
export interface AdQualitySettings {
  global_blocked_categories?: string[]
  global_blocked_advertisers?: string[]
  global_blocked_apps?: string[]
  default_safety_level?: string
}

// Supply Chain Settings
export interface SupplyChainSettings {
  schain_complete?: number
  schain_asi?: string
  schain_sid?: string
  tag_id?: string
  sellers_json_enabled?: boolean
}

// Revenue Settings
export interface RevenueSettings {
  // Platform-controlled (read-only for tenants)
  platform_fee_pct?: number // Platform fee percentage (set by platform admin, from tenant table)
  // Tenant-configurable
  default_rev_share?: number // Default revenue share for supplies (what tenant pays to publishers)
  default_floor_price?: number
  enable_dynamic_floor?: boolean
}

// Performance Settings
export interface PerformanceSettings {
  default_max_connections?: number
  default_keep_alive_ms?: number
  enable_gzip?: boolean
  circuit_breaker_threshold?: number
  circuit_breaker_timeout_ms?: number
}

// Notification/Alert Settings
export interface NotificationSettings {
  alert_email?: string
  webhook_url?: string
  low_fill_rate_threshold?: number
  high_error_rate_threshold?: number
}

// Privacy & Compliance Settings
export interface PrivacySettings {
  // GDPR
  gdpr_enabled?: boolean
  gdpr_default_consent?: string // 'denied' | 'granted'
  gdpr_tcf_version?: string // '2.0' | '2.2'
  gdpr_strict_mode?: boolean
  gdpr_block_without_consent?: boolean
  gdpr_pass_consent_string?: boolean

  // CCPA
  ccpa_enabled?: boolean
  ccpa_honor_opt_out?: boolean
  ccpa_default_consent?: string
  ccpa_us_privacy_string?: string

  // COPPA
  coppa_enabled?: boolean
  coppa_block_child_directed?: boolean

  // GPP (Global Privacy Platform)
  gpp_enabled?: boolean
  gpp_sid_list?: string[]

  // Data Retention
  data_retention_days?: number
  log_retention_days?: number
  personal_data_anonymize?: boolean

  // Consent Handling
  consent_required_for_bidding?: boolean
  consent_string_validation?: boolean
  consent_pass_to_demand?: boolean

  // Geographic Auto-Detection
  geo_privacy_auto_detect?: boolean
  gdpr_applies_countries?: string[]
  ccpa_applies_regions?: string[]
}

// All settings grouped by category
export interface SettingsResponse {
  tenant?: TenantInfo
  branding: BrandingSettings
  platform: PlatformSettings // Legacy alias
  openrtb: OpenRTBSettings
  traffic: TrafficSettings
  ad_quality: AdQualitySettings
  supply_chain: SupplyChainSettings
  revenue: RevenueSettings
  performance: PerformanceSettings
  notification: NotificationSettings
  privacy: PrivacySettings
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

/**
 * Fetch all settings grouped by category
 */
export async function getSettings(): Promise<{ data: SettingsResponse }> {
  return api.get<{ data: SettingsResponse }>('/settings')
}

/**
 * Update multiple settings at once
 * @param settings - Key-value pairs of settings to update
 */
export async function updateSettings<T extends Record<string, unknown>>(
  settings: T,
): Promise<{ message: string }> {
  return api.put<{ message: string }>('/settings', settings)
}

// ============================================================================
// CATEGORY-SPECIFIC UPDATE HELPERS
// ============================================================================

export async function updateBrandingSettings(
  settings: BrandingSettings,
): Promise<{ message: string }> {
  return updateSettings(settings as unknown as Record<string, unknown>)
}

// Legacy alias
export async function updatePlatformSettings(
  settings: PlatformSettings,
): Promise<{ message: string }> {
  return updateBrandingSettings(settings)
}

export async function updateOpenRTBSettings(
  settings: OpenRTBSettings,
): Promise<{ message: string }> {
  return updateSettings(settings as unknown as Record<string, unknown>)
}

export async function updateTrafficSettings(
  settings: TrafficSettings,
): Promise<{ message: string }> {
  return updateSettings(settings as unknown as Record<string, unknown>)
}

export async function updateAdQualitySettings(
  settings: AdQualitySettings,
): Promise<{ message: string }> {
  return updateSettings(settings as unknown as Record<string, unknown>)
}

export async function updateSupplyChainSettings(
  settings: SupplyChainSettings,
): Promise<{ message: string }> {
  return updateSettings(settings as unknown as Record<string, unknown>)
}

export async function updateRevenueSettings(
  settings: RevenueSettings,
): Promise<{ message: string }> {
  return updateSettings(settings as unknown as Record<string, unknown>)
}

export async function updatePerformanceSettings(
  settings: PerformanceSettings,
): Promise<{ message: string }> {
  return updateSettings(settings as unknown as Record<string, unknown>)
}

export async function updateNotificationSettings(
  settings: NotificationSettings,
): Promise<{ message: string }> {
  return updateSettings(settings as unknown as Record<string, unknown>)
}

export async function updatePrivacySettings(
  settings: PrivacySettings,
): Promise<{ message: string }> {
  return updateSettings(settings as unknown as Record<string, unknown>)
}
